import Navigation from 'components/navigation';
import { ShareButtonLocal } from 'components/navigation/share/ShareButton';
import { Route } from 'config/routes';
import { useLocale } from 'locale';
import { noop } from 'lodash';
import React, { useRef, useState, useMemo } from 'react';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import * as Styled from './styled';
import PhoneIcon from 'assets/svg/phone-1-5.svg';
import { Button } from 'ds/components/button';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { MainTitle } from '../common/styled';
import { Info } from '../common/Info';
import { Navigation as ScrollNav } from '../common/Navigation';
import { connect } from 'react-redux';
import { setIsAgentOfficeContactPopupOpen } from 'store/state/app/actions';
import { HEADER_HEIGHT } from 'components/navigation/header.constants';
import { HeaderWrapper } from 'ds/components/Layout';
import { useRoute } from 'react-router5';
import { IOfficeHeaderInfo, PageDetails } from 'screens/AgentOfficePages/types';
import { useShareHandler } from '../common/useShareHandler';
import withEvents from 'analytics/withEvents';
import { State } from 'store/state';
import { agentPageDataSelector } from 'store/state/domainData/selectors/agentById';
import { officePageDataSelector } from 'store/state/domainData/selectors/officeById';
import { IAgentPageData, IOfficePageData } from 'utils/entities';
import { ShareType } from 'helpers/share-helpers';
import { shareMethodToEvent } from 'screens/AgentOfficePages/common/components/Social';
import Breadcrumbs from 'components/breadcrumbs';

interface HeaderProps extends IOfficeHeaderInfo {
  agentPageInfo: IAgentPageData;
  officePageInfo: IOfficePageData;
  onAgentContactModalOpen?: (props: HeaderProps, isAgent: boolean) => void;
}

const DesktopHeaderBase: React.FC<HeaderProps> = (props) => {
  const { t } = useLocale();
  const {
    title,
    logo,
    address,
    phone,
    sections,
    activeSection,
    setContactOpen,
    officeName,
    officeId,
    onAgentContactModalOpen,
    pageDetails,
    onShareClick = noop,
    onShareOptionClick = noop,
    onPhoneExpose = noop,
  } = props;
  const topGhostNode = useRef<HTMLDivElement>(null);
  const [ topGhostVisible, setTopGhostVisible ] = useState(false);
  const { route } = useRoute();
  const shareHandler = useShareHandler(title);
  const isAgent = route.name === Route.AgentPage;
  const event = isAgent ? 'agent_share_click' : 'office_share_click';
  const eventMethod = isAgent ? 'agent_share_method' : 'office_share_method';
  const category = isAgent ? 'agent' : 'office';
  const eventShareHandler = () => {
    onShareClick(event, category, 'sticky', pageDetails);
  };

  const breadcrumbs = useMemo(() => [ {
    label: t('madad.navigation.breadcrumbs.home'),
    routeName: Route.Home,
  }, {
    label: t('html.madadAgentSearch.title'),
    routeName: Route.MadadPage,
  }, ...(isAgent && officeId ? [ {
    label: officeName,
    routeName: Route.OfficePage,
    routeParams: { id: officeId, source: 'agent_page' },
  } ] : [] ), {
    label: title,
    isCurrent: true,
  } ], [ t, isAgent, officeId, officeName, title ]);

  useIntersectionObserver(([ entry ]) => {
    setTopGhostVisible(!entry.isIntersecting);
  }, [ topGhostNode ], { threshold: 0, rootMargin: `-${HEADER_HEIGHT * 2}px` });

  const handleContactOpen = () => {
    setContactOpen(true);
    onAgentContactModalOpen(props, isAgent);
  };

  const handleShareMethod = (method: ShareType) => {
    onShareOptionClick(eventMethod, category, 'sticky', pageDetails, shareMethodToEvent[method]);
    shareHandler(method);
  };

  const handleStatisPhoneClickEvent = () => {
    onPhoneExpose(isAgent ? 'agent_page' : 'office_page', props);
  };

  return (
    <>
      <HeaderWrapper>
        <Navigation
          isMobileMapViewActive={false}
          setMobileMapViewActive={noop}
        />
      </HeaderWrapper>
      <Styled.ContentWrapper>
        <Styled.Wrapper>
          <Styled.Breadcrumbs>
            <Breadcrumbs items={breadcrumbs} />
          </Styled.Breadcrumbs>
          <Styled.HiddenWrapper isVisible={topGhostVisible}>
            <MainTitle withMaxWidth>{title}{isAgent ? <Styled.OfficeNameWrapper> · {officeName}</Styled.OfficeNameWrapper> : null}</MainTitle>
            <ShareButtonLocal
              shouldStayOpen={() => false}
              shouldResetSelectedValue={() => false}
              handleShare={handleShareMethod}
            >
              <div onClick={eventShareHandler}>
                <Styled.ShareIcon />
              </div>
            </ShareButtonLocal>
          </Styled.HiddenWrapper>
          <Styled.BtnWrapper>
            <Styled.HiddenWrapper isVisible={topGhostVisible}>
              <Styled.ContactSection>
                {phone ? (
                  <Styled.PhoneButton onClick={handleStatisPhoneClickEvent} href={createPhoneURI(phone)}>
                    {phone}
                    <PhoneIcon />
                  </Styled.PhoneButton>
                ) : null}
                {phone ? <Styled.TextBetween>{t('contactForm.contact.form.formButton.description')}</Styled.TextBetween> : null}
                <Button size="medium" onClick={handleContactOpen}>{t('developerPage.contactButton', { submitted: false, isMobile: false })}</Button>
              </Styled.ContactSection>
            </Styled.HiddenWrapper>
          </Styled.BtnWrapper>
        </Styled.Wrapper>
        {route.name === Route.OfficePage ? <Info {...{ title, logo, phone, about: address, setContactOpen: handleContactOpen }} pageDetails={pageDetails} onPhoneClickEventHandler={handleStatisPhoneClickEvent} /> : null}
      </Styled.ContentWrapper>
      <div ref={topGhostNode} />
      {route.name === Route.AgentPage ? null : <ScrollNav sections={sections} activeSection={activeSection} isSticky={topGhostVisible} pageDetails={pageDetails} />}
    </>
  );
};

const mapDispatchToProps = {
  setContactOpen: setIsAgentOfficeContactPopupOpen,
};

const DesktopHeaderWithEvents = withEvents<HeaderProps>((sendEvent) => ({
  onAgentContactModalOpen: (props: HeaderProps, isAgent: boolean) => {
    sendEvent('user_contact_me_form_open', 'user', {
      event: {
        agent_details: isAgent ? {
          id: props.agentPageInfo.agentId,
          name: props.agentPageInfo.name,
          office_id: props.agentPageInfo.officeId,
          office_name: props.officePageInfo.officeName,
          phone_number: props.phone,
        } : {
          office_id: props.officePageInfo.officeId,
          office_name: props.officePageInfo.officeName,
          phone_number: props.phone,
        },
        source: isAgent ? 'agent_page' : 'office_page',
      },
    });
  },
  onShareClick: (event: string, category: string, source: string, pageDetails: PageDetails) => {
    sendEvent(event, category, {
      ...pageDetails,
      event: {
        source,
      },
    });
  },
  onShareOptionClick: (event: string, category: string, source: string, pageDetails: PageDetails, method: string) => {
    sendEvent(event, category, {
      ...pageDetails,
      event: {
        source,
        share_method: method,
      },
    });
  },
  onPhoneExpose: (source: string, props: HeaderProps) => {
    sendEvent('user_contact_me_call_submit', 'user', {
      ...('agent_page' === source ? {
        property: {
          property_id: 'property_id',
          deal_type: 'for_sale',
          bulletin_info: {
            agent_details: {
              id: props.agentPageInfo.agentId,
              name: props.agentPageInfo.name,
              office_id: props.agentPageInfo.officeId,
              office_name: props.officePageInfo.officeName,
              phone_number: props.phone,
              is_leading_agent: props.isLeadingAgent,
            },
          },
        },
      } : {}),
      event: {
        source,
      },
      rtbData: {
        phone: props.phone,
      },
    });
  },
}))(DesktopHeaderBase);

const mapStateToProps = (state: State) => ({
  agentPageInfo: agentPageDataSelector(state),
  officePageInfo: officePageDataSelector(state),
});


export const DesktopHeader = connect(mapStateToProps, mapDispatchToProps)(DesktopHeaderWithEvents);

DesktopHeader.defaultProps = {
  onAgentContactModalOpen: noop,
};
